import { FieldValues, RegisterOptions } from "react-hook-form";

export type ValidationOptions = {
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  minValue?: number;
  maxValue?: number;
  formatValidation?: RegExp;
  customMessages?: Record<string, string>;
};

export type ControlRules = Omit<
  RegisterOptions<FieldValues, string>,
  "disabled" | "valueAsNumber" | "valueAsDate" | "setValueAs"
>;

export const requiredValidation = (): string => {
  return `This field is required`;
};

export const maxLengthValidation = (length: number): string => {
  return `This field should not exceed ${length} characters`;
};

export const minLengthValidation = (length: number): string => {
  return `This field should be at least ${length} characters`;
};

export const maxValueValidation = (value: number): string => {
  return `This field must be ${value} or less`;
};

export const minValueValidation = (value: number): string => {
  return `This field must be at least ${value}`;
};

export const formatValidation = (): string => {
  return `Invalid format for this field`;
};

export const validationOptionsToControlRules = (
  fieldLabel: string,
  options: ValidationOptions
): ControlRules => {
  const rules: ControlRules = {};

  if (options.required) {
    rules.required = requiredValidation();
  }
  if (options.maxLength) {
    rules.maxLength = {
      value: options.maxLength,
      message:
        options.customMessages?.maxLength ||
        maxLengthValidation(options.maxLength),
    };
  }
  if (options.minLength) {
    rules.minLength = {
      value: options.minLength,
      message:
        options.customMessages?.minLength ||
        minLengthValidation(options.minLength),
    };
  }
  if (options.minValue) {
    rules.min = {
      value: options.minValue,
      message:
        options.customMessages?.minValue ||
        minValueValidation(options.minValue),
    };
  }
  if (options.maxValue) {
    rules.max = {
      value: options.maxValue,
      message:
        options.customMessages?.maxValue ||
        maxValueValidation(options.maxValue),
    };
  }
  if (options.formatValidation) {
    rules.validate = {
      isValidPattern: (value: string) => {
        if (!value) return true;

        const cleanValue = value.trim();
        return (
          options.formatValidation?.test(cleanValue) ||
          options.customMessages?.formatValidation ||
          formatValidation()
        );
      },
    };
  }

  return rules;
};
